/**
 * TRUS-732: the Case assignment section of the Edit program dialog.
 *
 * Super admins decide, per program, which field is the case manager of record,
 * what the role is called on screen, when a case counts as closed, and whether
 * an open case whose case manager is no longer on the list is flagged. Who is on
 * the list is set in staff setup (Users → assignments → "On this program's case
 * manager list"), so removing someone from the org takes them off every list.
 *
 * Saves on its own button, like Billing Activities: the program PUT never
 * carries this setting, and only super admins can write it (enforced server-side).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import useCustomFields from '@/hooks/useCustomFields';
import {
  caseAssignmentAPI,
  DEFAULT_CASE_ASSIGNMENT,
  type CaseAssignmentConfig,
  type CaseManagerOption,
} from '@/services/api/caseAssignment';

interface Props {
  programId: number;
}

const selectClass =
  'w-full h-9 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2 text-sm';

export function ProgramCaseAssignmentSection({ programId }: Props) {
  const { t } = useAppTranslation();
  const { fields } = useCustomFields();
  const [loaded, setLoaded] = useState<CaseAssignmentConfig | null | undefined>(undefined);
  const [draft, setDraft] = useState<CaseAssignmentConfig>(DEFAULT_CASE_ASSIGNMENT);
  const [on, setOn] = useState(false);
  const [caseManagers, setCaseManagers] = useState<CaseManagerOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([caseAssignmentAPI.getConfig(programId), caseAssignmentAPI.listCaseManagers(programId)])
      .then(([config, cms]) => {
        if (cancelled) return;
        setLoaded(config);
        setOn(!!config);
        setDraft(config ?? DEFAULT_CASE_ASSIGNMENT);
        setCaseManagers(cms);
      })
      .catch(() => {
        if (!cancelled) setLoaded(null);
      });
    return () => {
      cancelled = true;
    };
  }, [programId]);

  // Status fields a program can close on: single-choice fields with options.
  const statusFields = useMemo(
    () => (fields || []).filter((f) => f.type === 'select' && Array.isArray(f.field_options) && f.field_options.length > 0),
    [fields],
  );
  const rule = draft.closed_rule;
  const statusField = rule.source === 'custom_field' ? statusFields.find((f) => f.id === rule.field_id) : undefined;

  const set = (patch: Partial<CaseAssignmentConfig>) => setDraft((d) => ({ ...d, ...patch }));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const saved = await caseAssignmentAPI.setConfig(programId, on ? draft : null);
      setLoaded(saved);
      setMessage({ kind: 'ok', text: t('caseAssignment.saved', 'Case assignment saved.') });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setMessage({
        kind: 'error',
        text: typeof detail === 'string' ? detail : t('caseAssignment.saveFailed', 'Could not save case assignment.'),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loaded === undefined) {
    return <Loader2 className="w-4 h-4 animate-spin text-gray-400" aria-label={t('caseAssignment.loading', 'Loading')} />;
  }

  return (
    <div className="space-y-4" data-testid="program-case-assignment">
      <div>
        <Label className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {t('caseAssignment.title', 'Case assignment')}
        </Label>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          {t(
            'caseAssignment.intro',
            'Choose where this program stores the case manager and what to call them. The people who can be picked are the staff ticked "On this program\'s case manager list" in their program assignments.',
          )}
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={on} onCheckedChange={(v) => setOn(v === true)} />
        {t('caseAssignment.enable', 'Use case assignment for this program')}
      </label>

      {on && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="ca-label">{t('caseAssignment.label', 'Name shown on screen')}</Label>
            <Input
              id="ca-label"
              value={draft.label}
              maxLength={60}
              onChange={(e) => set({ label: e.target.value })}
              placeholder={t('caseAssignment.labelPlaceholder', 'e.g. Case Manager, Care Coordinator, DCM')}
            />
          </div>

          <div>
            <Label htmlFor="ca-field">{t('caseAssignment.field', 'Stored on')}</Label>
            <select
              id="ca-field"
              className={selectClass}
              value={draft.assignee_field}
              onChange={(e) => set({ assignee_field: e.target.value as CaseAssignmentConfig['assignee_field'] })}
            >
              <option value="enrollment">{t('caseAssignment.fieldEnrollment', "The member's enrollment in this program")}</option>
              <option value="member_case_manager">{t('caseAssignment.fieldMember', "The member's Case Manager (one per member)")}</option>
            </select>
          </div>

          <div>
            <Label htmlFor="ca-closed">{t('caseAssignment.closedWhen', 'A case is closed when')}</Label>
            <select
              id="ca-closed"
              className={selectClass}
              value={rule.source === 'custom_field' ? String(rule.field_id) : 'enrollment_status'}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'enrollment_status') set({ closed_rule: { source: 'enrollment_status' } });
                else set({ closed_rule: { source: 'custom_field', field_id: Number(v), closed_values: [] } });
              }}
            >
              <option value="enrollment_status">
                {t('caseAssignment.closedEnrollment', 'The enrollment is Completed or Cancelled')}
              </option>
              {statusFields.map((f) => (
                <option key={f.id} value={String(f.id)}>
                  {t('caseAssignment.closedField', '{{field}} has a closed value', { field: f.friendly_name })}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm self-end pb-2">
            <Checkbox checked={draft.flag_reassignment} onCheckedChange={(v) => set({ flag_reassignment: v === true })} />
            {t('caseAssignment.flag', 'Flag open cases whose case manager is no longer on the list')}
          </label>

          {rule.source === 'custom_field' && statusField && (
            <div className="md:col-span-2">
              <Label>{t('caseAssignment.closedValues', 'Closed values')}</Label>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1">
                {(statusField.field_options || []).map((o) => (
                  <label key={o.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={rule.closed_values.includes(o.value)}
                      onCheckedChange={(v) =>
                        set({
                          closed_rule: {
                            ...rule,
                            closed_values: v === true
                              ? [...rule.closed_values, o.value]
                              : rule.closed_values.filter((x) => x !== o.value),
                          },
                        })
                      }
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="md:col-span-2 text-sm text-gray-600 dark:text-gray-400">
            {caseManagers.length === 0
              ? t('caseAssignment.noneOnList', 'No staff are on this program\'s case manager list yet. Tick them in each person\'s program assignments.')
              : t('caseAssignment.onList', 'On the list now: {{names}}', { names: caseManagers.map((c) => c.name).join(', ') })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('caseAssignment.save', 'Save case assignment')}
        </Button>
        {message && (
          <span className={message.kind === 'ok' ? 'text-sm text-green-700' : 'text-sm text-red-600'}>{message.text}</span>
        )}
      </div>
    </div>
  );
}

export default ProgramCaseAssignmentSection;
